import { useState, useRef, useCallback } from "react";
import { UserAvatar } from "../UserAvatar";
import { Paperclip, UserPlus, Share2, Heart } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { HoverToolbar } from "./HoverToolbar";
import { MobileChatActions } from "./MobileChatActions";
import { InlineQuote } from "./InlineQuote";
import { ReactionPills } from "./ReactionPills";
import { EmojiPicker } from "./EmojiPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DocChatItem } from "@/lib/erp/docs";

const DELETE_WINDOW_MS = 15 * 60 * 1000;

type ChatBubbleProps = {
  item: DocChatItem;
  isMine: boolean;
  isFirstInGroup: boolean;
  isActivity: boolean;
  onDelete?: (commentName: string) => void;
  onReact?: (commentName: string, emoji: string) => void;
  onReply?: (item: DocChatItem) => void;
  registerMessageRef?: (name: string, el: HTMLElement | null) => void;
  scrollToMessage?: (commentName: string) => void;
};

// ── Helpers ───────────────────────────────────────────────────

function formatTimestamp(creation: string): string {
  try {
    const ts = creation.endsWith("Z") ? creation : creation + "Z";
    return formatDistanceToNow(parseISO(ts), { addSuffix: true });
  } catch {
    return creation;
  }
}

function isWithinDeleteWindow(creation: string): boolean {
  const ts = creation.endsWith("Z") ? creation : creation + "Z";
  return Date.now() - new Date(ts).getTime() < DELETE_WINDOW_MS;
}

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function getActivityIcon(eventType?: string) {
  if (!eventType) return null;
  const lower = eventType.toLowerCase();
  if (lower.includes("attachment") || lower.includes("file")) return Paperclip;
  if (lower.includes("assigned") || lower.includes("assignment")) return UserPlus;
  if (lower.includes("shared") || lower.includes("unshared")) return Share2;
  if (lower.includes("like")) return Heart;
  return null;
}

function processActivityContent(content: string): string {
  if (!content) return "";
  return content
    .replace(
      /<strong>([^<]+)<\/strong>/gi,
      '<strong class="text-foreground font-semibold">$1</strong>'
    )
    .replace(
      /<b>([^<]+)<\/b>/gi,
      '<b class="text-foreground font-semibold">$1</b>'
    );
}

// ── Mention chips ──────────────────────────────────────────────

function MentionChips({ mentions }: { mentions: NonNullable<DocChatItem["mentions_detail"]> }) {
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {mentions.map((m) => (
        <span
          key={m.user}
          className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-[11px] text-blue-700 px-2 py-0.5 dark:bg-blue-900/30 dark:text-blue-200"
        >
          <UserAvatar
            imageUrl={m.avatar}
            fallbackText={m.full_name}
            size="sm"
            className="h-4 w-4"
          />
          @{m.full_name}
        </span>
      ))}
    </div>
  );
}

// ── Activity pill ─────────────────────────────────────────────

function ActivityBubble({ item }: { item: DocChatItem }) {
  const IconComponent = item.meta?.event_type
    ? getActivityIcon(item.meta.event_type)
    : null;

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-muted-foreground">
        {formatTimestamp(item.creation)}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded px-3 py-1.5 inline-flex max-w-full">
        {IconComponent && (
          <IconComponent className="h-3.5 w-3.5 flex-shrink-0" />
        )}
        <div className="flex items-center">
          {item.meta?.event_type && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mr-1.5">
              {item.meta.event_type}
            </span>
          )}
          <span
            dangerouslySetInnerHTML={{
              __html: processActivityContent(item.content),
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirmation ───────────────────────────────────────

function DeleteConfirmation({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <PopoverContent
      side="top"
      align="end"
      className="w-auto p-3"
      onPointerDownOutside={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <p className="text-sm mb-3">Delete this message?</p>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="destructive" size="sm" onClick={onConfirm}>
          Delete
        </Button>
      </div>
    </PopoverContent>
  );
}

// ── Main export ───────────────────────────────────────────────

export function ChatBubble({
  item,
  isMine,
  isFirstInGroup,
  isActivity,
  onDelete,
  onReact,
  onReply,
  registerMessageRef,
  scrollToMessage,
}: ChatBubbleProps) {
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (isActivity) {
    return <ActivityBubble item={item} />;
  }

  const hasMentions = item.mentions_detail && item.mentions_detail.length > 0;
  const hasReactions = item.reactions && item.reactions.length > 0;
  const canDelete = isMine && isWithinDeleteWindow(item.creation);
  const plainText = item.plain_text || stripHtml(item.content);

  const handleEmojiSelect = (emoji: string) => {
    onReact?.(item.name, emoji);
  };

  const handleReactionToggle = (emoji: string) => {
    onReact?.(item.name, emoji);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    onDelete?.(item.name);
  };

  const handleTouchStart = useCallback(() => {
    longPressTimerRef.current = setTimeout(() => {
      setMobileActionsOpen(true);
    }, 500);
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const replyDetail = item.reply_to_detail;
  const inlineQuote = replyDetail ? (
    <InlineQuote
      authorName={replyDetail.user_full_name}
      content={replyDetail.content}
      isDeleted={replyDetail.is_deleted}
      onClick={
        !replyDetail.is_deleted && scrollToMessage
          ? () => scrollToMessage(replyDetail.name)
          : undefined
      }
    />
  ) : null;

  const bubbleContent = (
    <div
      className={cn(
        "rounded-2xl px-3 py-2 text-sm prose prose-sm max-w-none dark:prose-invert",
        isMine
          ? "bg-primary/10 text-foreground"
          : "bg-muted text-foreground"
      )}
    >
      {inlineQuote}
      <div dangerouslySetInnerHTML={{ __html: item.content }} />
    </div>
  );

  const toolbar = (
    <HoverToolbar
      canDelete={canDelete}
      plainText={plainText}
      onReact={onReact ? () => setEmojiPickerOpen(true) : undefined}
      onReply={onReply ? () => onReply(item) : undefined}
      onDelete={canDelete ? () => setShowDeleteConfirm(true) : undefined}
    />
  );

  const reactionPills = hasReactions ? (
    <ReactionPills reactions={item.reactions!} onToggle={handleReactionToggle} />
  ) : null;

  const deletePopover = showDeleteConfirm ? (
    <Popover open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <PopoverTrigger asChild>
        <span className="sr-only">Delete trigger</span>
      </PopoverTrigger>
      <DeleteConfirmation
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </Popover>
  ) : null;

  const mobileActions = (
    <MobileChatActions
      open={mobileActionsOpen}
      onClose={() => setMobileActionsOpen(false)}
      canDelete={canDelete}
      plainText={plainText}
      onReact={onReact ? () => { setMobileActionsOpen(false); setEmojiPickerOpen(true); } : undefined}
      onReply={onReply ? () => onReply(item) : undefined}
      onDelete={canDelete ? () => { setMobileActionsOpen(false); setShowDeleteConfirm(true); } : undefined}
    />
  );

  const emojiPicker = onReact ? (
    <EmojiPicker
      open={emojiPickerOpen}
      onOpenChange={setEmojiPickerOpen}
      onSelect={handleEmojiSelect}
      onClose={() => setEmojiPickerOpen(false)}
    >
      <span className="sr-only">Emoji picker trigger</span>
    </EmojiPicker>
  ) : null;

  if (isMine) {
    return (
      <div
        ref={(el) => registerMessageRef?.(item.name, el)}
        className={cn("flex justify-end group/bubble relative transition-colors duration-500", isFirstInGroup ? "" : "mt-1")}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex flex-col items-end max-w-[75%] relative">
          {isFirstInGroup && (
            <div className="text-[11px] text-muted-foreground mb-1">
              {formatTimestamp(item.creation)}
            </div>
          )}
          <div className="relative">
            <div className="hidden md:block">{toolbar}</div>
            {bubbleContent}
            {deletePopover}
            {emojiPicker}
          </div>
          {reactionPills}
          {hasMentions && <MentionChips mentions={item.mentions_detail!} />}
        </div>
        {mobileActions}
      </div>
    );
  }

  // Other user's message
  return (
    <div
      ref={(el) => registerMessageRef?.(item.name, el)}
      className={cn(
        "flex gap-3 justify-start group/bubble relative transition-colors duration-500",
        isFirstInGroup ? "" : "mt-1 pl-11"
      )}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {isFirstInGroup && (
        <div className="flex-shrink-0">
          <UserAvatar
            imageUrl={item.user_avatar}
            fallbackText={item.user_full_name || item.owner}
            size="md"
            className="h-8 w-8"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {isFirstInGroup && (
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[13px] font-semibold text-foreground">
              {item.user_full_name || item.owner}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {formatTimestamp(item.creation)}
            </span>
          </div>
        )}
        <div className="relative">
          <div className="hidden md:block">{toolbar}</div>
          {bubbleContent}
          {deletePopover}
          {emojiPicker}
        </div>
        {reactionPills}
        {hasMentions && <MentionChips mentions={item.mentions_detail!} />}
      </div>
      {mobileActions}
    </div>
  );
}
